<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useMessageContext } from '../provider.js';
import { MESSAGE_VARIANTS } from '../constants';
import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';

const { attachments, variant } = useMessageContext();

// advup — anexo em mensagem privada precisa ficar visualmente óbvio (mesmo
// âmbar do texto privado), senão parece indistinguível de um anexo que
// realmente foi pro cliente. Achado 2026-07-21 (Victor).
const isPrivate = computed(() => variant.value === MESSAGE_VARIANTS.PRIVATE);

const { t } = useI18n();

const attachment = computed(() => attachments.value[0] || {});

const url = computed(() => attachment.value.dataUrl);

const fileName = computed(() => {
  if (url.value) {
    const filename = url.value.substring(url.value.lastIndexOf('/') + 1);
    return filename || t('CONVERSATION.UNKNOWN_FILE_TYPE');
  }
  return t('CONVERSATION.UNKNOWN_FILE_TYPE');
});

const fileType = computed(() => {
  return (attachment.value.extension || fileName.value.split('.').pop() || '')
    .toString()
    .toLowerCase();
});

const fileSizeLabel = computed(() => {
  const bytes = Number(attachment.value.fileSize || 0);
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
});

const metaLabel = computed(() => {
  const parts = [];
  if (fileType.value) parts.push(fileType.value.toUpperCase());
  if (fileSizeLabel.value) parts.push(fileSizeLabel.value);
  return parts.join(' · ');
});
</script>

<template>
  <a
    :href="url"
    target="_blank"
    rel="noopener noreferrer"
    download
    class="flex items-center gap-3 min-w-[15rem] max-w-[20rem] p-2.5 rounded-xl no-underline transition-colors group/file"
    :class="
      isPrivate
        ? 'bg-n-solid-amber border border-dashed border-n-amber-9 hover:bg-n-solid-amber/70'
        : 'bg-n-solid-1 border border-n-weak hover:bg-n-alpha-1'
    "
  >
    <span
      class="flex items-center justify-center size-10 rounded-lg shrink-0"
      :class="isPrivate ? 'bg-n-amber-5' : 'bg-n-alpha-2'"
    >
      <FileIcon :file-type="fileType" class="size-6" />
    </span>
    <span class="flex flex-col min-w-0 flex-1">
      <span
        class="text-sm font-medium truncate"
        :class="isPrivate ? 'text-n-amber-12' : 'text-n-slate-12'"
      >
        {{ decodeURI(fileName) }}
      </span>
      <span
        v-if="metaLabel"
        class="text-xs mt-0.5"
        :class="isPrivate ? 'text-n-amber-12/70' : 'text-n-slate-11'"
      >
        {{ metaLabel }}
      </span>
    </span>
    <Icon
      icon="i-ph-download-simple"
      class="size-4 shrink-0 transition-colors"
      :class="
        isPrivate
          ? 'text-n-amber-12/70 group-hover/file:text-n-amber-12'
          : 'text-n-slate-11 group-hover/file:text-n-slate-12'
      "
    />
  </a>
</template>
